import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';

export type LogoVariant = 'full' | 'icon';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';

export interface BrandLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

const sizeMap: Record<LogoSize, string> = {
  sm: 'h-6',     // 24px
  md: 'h-8',     // 32px
  lg: 'h-14',    // 56px
  xl: 'h-20',    // 80px
};

export function BrandLogo({
  variant = 'full',
  size = 'md',
  className = '',
  href,
  onClick,
}: BrandLogoProps) {
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const logoSrc = '/assets/invitecloud-logo.png';
  const iconSrc = '/assets/ic-monogram.png';
  const src = variant === 'icon' ? iconSrc : logoSrc;
  const sizeClass = sizeMap[size];

  const targetHref = href && href !== '/'
    ? href
    : isAuthenticated
      ? '/dashboard'
      : '/login';

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
      return;
    }
    e.preventDefault();
    navigate(targetHref);
  };

  const img = (
    <img
      src={src}
      alt="InviteCloud"
      onError={() => setImageError(true)}
      className={`${sizeClass} w-auto ${className}`}
    />
  );

  const fallback = (
    <div className={`${sizeClass} px-2 bg-primary/10 rounded-md flex items-center justify-center text-primary font-bold text-xs`}>
      IC
    </div>
  );

  return (
    <a
      href={targetHref}
      onClick={handleClick}
      aria-label="InviteCloud Home"
      className="hover:opacity-80 transition-opacity flex items-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
    >
      {imageError ? fallback : img}
    </a>
  );
}
